/**
 * Variant Image Filter for Flickity
 * Filters product carousel images by variant without destroying Flickity
 *
 * Replaces StarApps functionality that was breaking video drag
 * Author: Claude Code
 * Date: 2025-11-13
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    selectors: {
      productSlideshow: '[data-product-slideshow]',
      productSlide: '.product__slide',
      productContainer: '[data-product]',
      productImage: '[data-product-image]'
    },
    classes: {
      slideHidden: 'product__slide--hidden',
      flickityEnabled: 'flickity-enabled'
    },
    debug: true // Set to false in production
  };

  /**
   * Main VariantImageFilter class
   */
  class VariantImageFilter {
    constructor(container) {
      this.container = container;
      this.slideshow = container.querySelector(CONFIG.selectors.productSlideshow);
      this.flickity = null;
      this.allSlides = [];
      this.variantImageMap = {};
      this.productHandle = null;
      this.product = null;
      this.initialized = false;

      if (!this.slideshow) {
        this.log('No slideshow found in container');
        return;
      }

      this.init();
    }

    /**
     * Initialize the filter
     */
    init() {
      // Wait for Flickity to be initialized
      this.waitForFlickity().then(() => {
        this.log('Flickity detected, initializing variant filter');
        this.detectConflictingScripts();
        this.setup();
      }).catch((error) => {
        this.log('Failed to initialize:', error);
      });
    }

    /**
     * Detect and warn about conflicting scripts
     */
    detectConflictingScripts() {
      const warnings = [];

      // Check for StarApps script
      const starappsScript = document.querySelector('script[src*="starapps"]');
      if (starappsScript) {
        warnings.push('⚠️  StarApps script detected - may conflict with this filter');
      }

      // Check for hidden slides that shouldn't be hidden
      const hiddenSlides = this.slideshow.querySelectorAll('.product__slide[style*="display: none"]');
      if (hiddenSlides.length > 0) {
        warnings.push('⚠️  ' + hiddenSlides.length + ' slides are already hidden by another script');
      }

      if (warnings.length > 0) {
        this.log('Potential conflicts detected:');
        warnings.forEach(w => this.log(w));
        this.log('Attempting to show all slides...');
      }
    }

    /**
     * Wait for Flickity to be initialized
     */
    waitForFlickity() {
      return new Promise((resolve, reject) => {
        const maxAttempts = 300; // Increased to 30 seconds
        let attempts = 0;

        const checkFlickity = () => {
          attempts++;

          // Try multiple methods to get Flickity instance
          if (window.Flickity) {
            // Method 1: Check if classList contains flickity-enabled
            if (this.slideshow.classList.contains(CONFIG.classes.flickityEnabled)) {
              this.flickity = Flickity.data(this.slideshow);
              if (this.flickity) {
                this.log('Flickity found after ' + attempts + ' attempts (method 1)');
                resolve();
                return;
              }
            }

            // Method 2: Try direct property access
            if (this.slideshow.flickityData) {
              this.flickity = this.slideshow.flickityData;
              this.log('Flickity found after ' + attempts + ' attempts (method 2)');
              resolve();
              return;
            }
          }

          if (attempts >= maxAttempts) {
            this.log('Warning: Flickity not found, but continuing with setup');
            resolve(); // Don't reject, continue anyway
            return;
          }

          setTimeout(checkFlickity, 100);
        };

        checkFlickity();
      });
    }

    /**
     * Setup the filter after Flickity is ready
     */
    setup() {
      // CRITICAL: First, remove media--hidden from ALL slides immediately
      const allDomSlides = Array.from(this.slideshow.querySelectorAll(CONFIG.selectors.productSlide));
      allDomSlides.forEach((slide) => {
        if (slide.classList.contains('media--hidden')) {
          slide.classList.remove('media--hidden');
          this.log('Removed media--hidden from slide during setup');
        }
      });

      // Get slides from Flickity cells instead of DOM
      // This is crucial because other scripts may hide slides in DOM
      if (this.flickity && this.flickity.cells) {
        this.allSlides = this.flickity.cells.map(cell => cell.element);
        this.log('Found ' + this.allSlides.length + ' slides from Flickity cells');
      } else {
        // Fallback to DOM if Flickity cells not available
        this.allSlides = allDomSlides;
        this.log('Found ' + this.allSlides.length + ' slides from DOM (fallback)');
      }

      // Add data-slide-index for debugging
      if (CONFIG.debug) {
        this.allSlides.forEach((slide, index) => {
          slide.setAttribute('data-slide-index', index);
        });
      }

      // Build variant-to-images map
      this.buildVariantImageMap();

      // CRITICAL: ALWAYS show all slides on init to remove server-rendered media--hidden
      // The server renders slides with media--hidden by default, we MUST remove it
      this.log('Removing server-rendered media--hidden from all slides');
      this.showAllSlides();

      // Setup MutationObserver to prevent other scripts from hiding slides
      this.setupMediaHiddenObserver();

      // Listen for variant changes
      this.container.addEventListener('theme:variant:change', (event) => {
        this.handleVariantChange(event);
      });

      this.initialized = true;
      this.log('Variant image filter initialized');
    }

    /**
     * Setup MutationObserver to prevent other scripts from adding media--hidden
     * This fights against Shopify's native variant filtering
     */
    setupMediaHiddenObserver() {
      if (!window.MutationObserver) {
        this.log('MutationObserver not available');
        return;
      }

      this.log('Setting up media--hidden observer to prevent conflicts');

      // Create observer to watch for media--hidden being added
      this.observer = new MutationObserver((mutations) => {
        let needsUpdate = false;

        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;

            // If media--hidden was added, remove it
            if (target.classList.contains('media--hidden') &&
                target.classList.contains('product__slide')) {
              target.classList.remove('media--hidden');
              needsUpdate = true;
            }
          }
        });

        // Reload Flickity if we removed media--hidden from any slides
        if (needsUpdate && this.flickity) {
          this.flickity.reloadCells();
          this.flickity.resize();
        }
      });

      // Observe all product slides
      this.allSlides.forEach((slide) => {
        this.observer.observe(slide, {
          attributes: true,
          attributeFilter: ['class']
        });
      });

      this.log('Observer active on ' + this.allSlides.length + ' slides');
    }

    /**
     * Build a map of variant IDs to image indices
     * This can be customized based on your product data structure
     */
    buildVariantImageMap() {
      // Try to get product JSON
      const productJsonElement = this.container.querySelector('[data-product-json]');
      if (!productJsonElement) {
        this.log('No product JSON found');
        return;
      }

      try {
        this.product = JSON.parse(productJsonElement.innerHTML);
        this.productHandle = this.product.handle;
        this.log('Product loaded:', this.product.title, '('+this.productHandle+')');

        // Check for manual configuration
        const manualConfig = window.getVariantImageConfig && window.getVariantImageConfig(this.productHandle);

        if (manualConfig) {
          this.log('Using manual configuration for product');
          this.buildMapFromManualConfig(manualConfig);
          this.log('Variant image map built:', this.variantImageMap);
        } else {
          this.log('No manual config found - showing all images by default');
          this.log('To filter images, add configuration in variant-image-filter-config.js');
          // Don't build any map - this will show all images
        }
      } catch (error) {
        this.log('Error parsing product JSON:', error);
      }
    }

    /**
     * Build map from manual configuration
     */
    buildMapFromManualConfig(config) {
      if (config.variants) {
        // Direct variant ID mapping
        this.variantImageMap = { ...config.variants };
        this.log('Using direct variant mapping');
      } else if (config.options) {
        // Map by option values
        this.product.variants.forEach((variant) => {
          const imageIndices = [];

          // For each option (Color, Size, etc.)
          Object.keys(config.options).forEach((optionName) => {
            // Find which option index this is (option1, option2, option3)
            const optionIndex = this.product.options.findIndex(opt => opt === optionName);

            if (optionIndex !== -1) {
              const optionValue = variant['option' + (optionIndex + 1)];
              const optionConfig = config.options[optionName];

              if (optionConfig && optionConfig[optionValue]) {
                imageIndices.push(...optionConfig[optionValue]);
              }
            }
          });

          if (imageIndices.length > 0) {
            // Remove duplicates and sort
            this.variantImageMap[variant.id] = [...new Set(imageIndices)].sort((a, b) => a - b);
          }
        });

        this.log('Using option-based mapping');
      }
    }

    /**
     * Build map from variant featured_media
     */
    buildMapFromFeaturedMedia() {
      this.product.variants.forEach((variant) => {
        if (variant.featured_media) {
          // Find the slide index for this media
          const mediaId = variant.featured_media.id;
          const slideIndex = this.findSlideIndexByMediaId(mediaId);

          if (slideIndex !== -1) {
            if (!this.variantImageMap[variant.id]) {
              this.variantImageMap[variant.id] = [];
            }
            this.variantImageMap[variant.id].push(slideIndex);
          }
        }
      });
    }

    /**
     * Find slide index by media ID
     */
    findSlideIndexByMediaId(mediaId) {
      for (let i = 0; i < this.allSlides.length; i++) {
        const slide = this.allSlides[i];
        const image = slide.querySelector(CONFIG.selectors.productImage);
        if (image && image.getAttribute('data-image-id') == mediaId) {
          return i;
        }
      }
      return -1;
    }

    /**
     * Handle variant change event
     */
    handleVariantChange(event) {
      const variant = event.detail.variant;
      if (!variant) {
        this.log('No variant in event');
        return;
      }

      this.log('Variant changed to:', variant.id, variant.name);
      this.filterSlidesByVariant(variant);
    }

    /**
     * Filter slides based on selected variant
     */
    filterSlidesByVariant(variant) {
      if (!this.initialized || !this.flickity) {
        this.log('Not initialized, skipping filter');
        return;
      }

      // Get the images for this variant
      const variantImageIndices = this.variantImageMap[variant.id] || [];

      // IMPORTANT: Only filter if we have manual configuration
      // Otherwise, show all images (default Shopify behavior)
      if (variantImageIndices.length === 0) {
        this.log('No mapping for variant, showing all slides (default behavior)');
        this.showAllSlides();

        // Navigate to featured_media if available
        if (variant.featured_media) {
          const mediaId = variant.featured_media.id;
          const slideIndex = this.findSlideIndexByMediaId(mediaId);
          if (slideIndex !== -1) {
            const flickityCellIndex = this.findFlickityCellIndex(slideIndex);
            if (flickityCellIndex !== -1) {
              this.flickity.select(flickityCellIndex, false, true);
              this.log('Navigated to featured_media at index', slideIndex);
            }
          }
        }
        return;
      }

      this.log('Showing slides for variant:', variantImageIndices);

      // Hide all slides first
      this.allSlides.forEach((slide, index) => {
        slide.classList.add(CONFIG.classes.slideHidden);
        slide.style.display = 'none';
      });

      // Show only slides for this variant
      variantImageIndices.forEach((index) => {
        if (index >= 0 && index < this.allSlides.length) {
          const slide = this.allSlides[index];
          slide.classList.remove(CONFIG.classes.slideHidden);
          slide.classList.remove('media--hidden');
          slide.style.display = '';
        }
      });

      // Reload Flickity cells and navigate to first visible slide
      this.flickity.reloadCells();
      this.flickity.resize();

      // Select the first visible slide
      if (variantImageIndices.length > 0) {
        const firstVisibleIndex = variantImageIndices[0];
        // Find the Flickity cell index for this slide
        const flickityCellIndex = this.findFlickityCellIndex(firstVisibleIndex);
        if (flickityCellIndex !== -1) {
          this.flickity.select(flickityCellIndex, false, true);
        }
      }

      this.log('Slides filtered successfully');
    }

    /**
     * Find Flickity cell index for a slide
     */
    findFlickityCellIndex(slideIndex) {
      if (!this.flickity || !this.flickity.cells) {
        return -1;
      }

      const slide = this.allSlides[slideIndex];
      if (!slide) {
        return -1;
      }

      for (let i = 0; i < this.flickity.cells.length; i++) {
        if (this.flickity.cells[i].element === slide) {
          return i;
        }
      }

      return -1;
    }

    /**
     * Show all slides
     */
    showAllSlides() {
      this.allSlides.forEach((slide) => {
        slide.classList.remove(CONFIG.classes.slideHidden);
        slide.classList.remove('media--hidden'); // Remove Shopify's native filter class
        slide.style.display = '';
      });

      if (this.flickity) {
        this.flickity.reloadCells();
        this.flickity.resize();
      }

      this.log('All slides shown');
    }

    /**
     * Log debug messages
     */
    log(...args) {
      if (CONFIG.debug && console && console.log) {
        console.log('[VariantImageFilter]', ...args);
      }
    }

    /**
     * Public API for debugging
     */
    status() {
      return {
        initialized: this.initialized,
        flickity: this.flickity ? 'loaded' : 'not found',
        totalSlides: this.allSlides.length,
        variantImageMap: this.variantImageMap
      };
    }

    /**
     * Manually filter by variant ID
     */
    filterByVariantId(variantId) {
      const productJsonElement = this.container.querySelector('[data-product-json]');
      if (productJsonElement) {
        const product = JSON.parse(productJsonElement.innerHTML);
        const variant = product.variants.find(v => v.id == variantId);
        if (variant) {
          this.filterSlidesByVariant(variant);
        }
      }
    }

    /**
     * Reset to show all slides
     */
    reset() {
      this.showAllSlides();
    }
  }

  /**
   * Initialize on product pages
   */
  function initVariantImageFilter() {
    const productContainers = document.querySelectorAll(CONFIG.selectors.productContainer);

    if (productContainers.length === 0) {
      return;
    }

    productContainers.forEach((container) => {
      const filter = new VariantImageFilter(container);

      // Expose to window for debugging
      if (!window.variantImageFilters) {
        window.variantImageFilters = [];
      }
      window.variantImageFilters.push(filter);
    });

    console.log('[VariantImageFilter] Initialized on', productContainers.length, 'product(s)');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantImageFilter);
  } else {
    initVariantImageFilter();
  }

  // Also initialize on Shopify section load (for Theme Editor)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector(CONFIG.selectors.productContainer)) {
      setTimeout(initVariantImageFilter, 500);
    }
  });

  // Public API for debugging in console
  window.VariantImageFilter = VariantImageFilter;
  window.variantImageFilterStatus = function() {
    if (window.variantImageFilters && window.variantImageFilters.length > 0) {
      return window.variantImageFilters.map(f => f.status());
    }
    return 'Not initialized';
  };

  // Force cache refresh - 2025-11-13

})();